/**
 * Global confirmation dialog.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   if (!(await confirm({
 *     title: 'Delete pool?',
 *     description: 'This removes the pool and all player entries.',
 *     confirmLabel: 'Delete',
 *     variant: 'destructive',
 *   }))) return;
 *
 * Mount <ConfirmProvider> once near the root of the app. It exposes
 * `useConfirm()` — a function that returns a Promise<boolean>.
 *
 * Variants: 'destructive' (red, warning icon) | 'warning' (amber) | 'default' (primary).
 */
import { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';

const ConfirmContext = createContext(null);

const VARIANT_STYLES = {
  destructive: {
    icon: AlertTriangle,
    iconClass: 'text-destructive',
    iconBgClass: 'bg-destructive/10',
    actionClass:
      'bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/40',
  },
  warning: {
    icon: AlertTriangle,
    iconClass: 'text-amber-600 dark:text-amber-400',
    iconBgClass: 'bg-amber-100 dark:bg-amber-950/40',
    actionClass:
      'bg-amber-600 text-white hover:bg-amber-700 focus-visible:ring-amber-600/40',
  },
  default: {
    icon: Info,
    iconClass: 'text-primary',
    iconBgClass: 'bg-primary/10',
    actionClass: '',
  },
  success: {
    icon: CheckCircle2,
    iconClass: 'text-emerald-600 dark:text-emerald-400',
    iconBgClass: 'bg-emerald-100 dark:bg-emerald-950/40',
    actionClass: 'bg-emerald-600 text-white hover:bg-emerald-700',
  },
};

const DEFAULT_OPTIONS = {
  title: 'Are you sure?',
  description: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  variant: 'default',
};

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    setOptions({ ...DEFAULT_OPTIONS, ...opts });
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleResolve = (result) => {
    setOpen(false);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    if (resolve) resolve(result);
  };

  const variant = VARIANT_STYLES[options.variant] || VARIANT_STYLES.default;
  const Icon = variant.icon;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) handleResolve(false);
        }}
      >
        <AlertDialogContent
          className="sm:max-w-md rounded-xl border-border/60 p-0 overflow-hidden"
          data-testid="confirm-dialog"
        >
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${variant.iconBgClass}`}
              >
                <Icon className={`h-5 w-5 ${variant.iconClass}`} aria-hidden="true" />
              </div>
              <AlertDialogHeader className="space-y-1.5 flex-1 text-left">
                <AlertDialogTitle
                  className="text-base font-semibold leading-tight"
                  data-testid="confirm-dialog-title"
                >
                  {options.title}
                </AlertDialogTitle>
                {options.description && (
                  <AlertDialogDescription
                    className="text-sm text-muted-foreground leading-relaxed"
                    data-testid="confirm-dialog-description"
                  >
                    {options.description}
                  </AlertDialogDescription>
                )}
              </AlertDialogHeader>
            </div>
          </div>
          <AlertDialogFooter className="bg-muted/40 border-t border-border/60 px-6 py-3 gap-2 sm:gap-2">
            <AlertDialogCancel
              onClick={() => handleResolve(false)}
              className="mt-0 h-9"
              data-testid="confirm-dialog-cancel"
            >
              {options.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleResolve(true)}
              className={`h-9 ${variant.actionClass}`}
              data-testid="confirm-dialog-confirm"
            >
              {options.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used inside <ConfirmProvider>');
  }
  return ctx;
}
